import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { googleLogin } from "@/lib/googleAuth";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Connexion réussie");
      // Admin has no league — go straight to admin dashboard
      const dest = res.ok && localStorage.getItem("yoles_token") ? "/" : "/";
      // We can't read user directly here; navigate to / which will redirect if admin
      navigate("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel - visual */}
      <div className="relative hidden md:block overflow-hidden bg-[color:var(--deep)]">
        <img
          src="/branding/logo_full.png"
          alt="Ma Petite Yole"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--deep)] via-[color:var(--deep)]/20 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-10 text-white">
          <div>
            <div className="brand-signature text-sm mb-4">Prono. Vibré. Gagné.</div>
            <h2 className="brand-wordmark text-white text-5xl mb-3">
              Parie sur la<br />
              <span className="text-[color:var(--sunshine)]">course</span> avec tes amis.
            </h2>
            <p className="text-sm opacity-80 max-w-xs">
              Pronostique le vainqueur, prédis les dessalages et grimpe au classement — le tout avec des points, entre amis.
            </p>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex items-center justify-center px-6 py-10 md:py-16 bg-[color:var(--seafoam)]">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <div className="md:hidden flex items-center gap-3 mb-6">
              <img src="/branding/app_icon.png" alt="" className="w-12 h-12 rounded-xl" />
              <div>
                <div className="brand-wordmark text-[color:var(--deep)] text-[22px] leading-none">Ma Petite Yole</div>
                <div className="brand-signature text-[10px] mt-1">Prono. Vibré. Gagné.</div>
              </div>
            </div>
            <h1 className="brand-wordmark text-4xl sm:text-5xl text-[color:var(--deep)]">
              Rebonjour.
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Connecte-toi pour placer tes paris.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                placeholder="ton@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-12 rounded-xl border-slate-200 bg-white"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Mot de passe
              </Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-12 rounded-xl border-slate-200 bg-white"
                required
              />
            </div>

            {error && (
              <div
                className="text-sm text-[color:var(--coral)] bg-red-50 border border-red-100 rounded-lg px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-12 rounded-full btn-cta text-sm transition-all hover:scale-[1.02]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
            </Button>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center"><span className="bg-[color:var(--seafoam)] px-3 text-[10px] uppercase tracking-widest font-bold text-slate-400">ou</span></div>
            </div>

            <button
              type="button"
              onClick={googleLogin}
              data-testid="google-login-btn"
              className="w-full h-12 rounded-full bg-white border-2 border-slate-200 hover:border-slate-400 font-bold text-sm text-slate-700 flex items-center justify-center gap-3 transition"
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continuer avec Google
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-slate-500">
            Pas encore de compte ?{" "}
            <Link
              to="/register"
              data-testid="go-register"
              className="font-bold text-[color:var(--coral)] hover:underline"
            >
              Inscris-toi
            </Link>
          </p>

          <nav className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
            <Link to="/cgu" className="hover:text-[color:var(--coral)]">CGU</Link>
            <span>·</span>
            <Link to="/confidentialite" className="hover:text-[color:var(--coral)]">Confidentialité</Link>
            <span>·</span>
            <Link to="/mentions-legales" className="hover:text-[color:var(--coral)]">Mentions</Link>
            <span>·</span>
            <Link to="/contact" className="hover:text-[color:var(--coral)]">Contact</Link>
          </nav>
        </form>
      </div>
    </div>
  );
}
